import { DateTime } from 'luxon';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import estimatePreviewBitrate from '@/helpers/estimate_preview_bitrate';

const INTERNAL_LINK_WARNING_SUPPRESS_WEEKS = 2;

const useSettingsStore = defineStore(
  'settings',
  () => {
    const allowControl = ref(false);
    const gridColumns = ref('auto');
    const headerPinned = ref(true);
    const tooltips = ref(true);

    // 'auto' | 'direct' | 'proxy' — see resolve_connection_mode.
    const connectionMode = ref('auto');
    // 'auto' (fastest by latency) | 'EU' | 'US', proxy mode only.
    const regionPreference = ref('auto');

    // When set to a future ISO timestamp, the "internal link needs VPN" warning is suppressed until
    // then (see InternalLinkWarningModal). Expires on its own so the warning re-appears eventually.
    const internalLinkWarnSuppressedUntil = ref(null);
    const warnBeforeInternalLinks = computed(() => {
      const until = internalLinkWarnSuppressedUntil.value;
      return !until || DateTime.fromISO(until) <= DateTime.now();
    });
    function suppressInternalLinkWarning() {
      internalLinkWarnSuppressedUntil.value = DateTime.now()
        .plus({ weeks: INTERNAL_LINK_WARNING_SUPPRESS_WEEKS })
        .toISO();
    }
    function clearInternalLinkWarningSuppression() {
      internalLinkWarnSuppressedUntil.value = null;
    }

    const previewWidth = ref(640);
    const previewQuality = ref('low');
    const previewEncoderType = ref('webp');
    const previewFps = ref(2);
    const previewEncoderQuality = ref(5);

    const audioMonitorMix = ref('LR');

    const monitorWidth = ref(1344);
    const monitorQuality = ref('medium');
    const monitorEncoderType = ref('webp');
    const monitorFps = ref(12);
    const monitorEncoderQuality = ref(30);

    const previewBitrate = computed(() =>
      estimatePreviewBitrate({
        encoderType: previewEncoderType,
        encoderQuality: previewEncoderQuality,
        fps: previewFps,
      }),
    );

    const monitorBitrate = computed(() =>
      estimatePreviewBitrate({
        encoderType: monitorEncoderType,
        encoderQuality: monitorEncoderQuality,
        fps: monitorFps,
      }),
    );

    return {
      allowControl,
      gridColumns,
      headerPinned,
      tooltips,

      connectionMode,
      regionPreference,

      internalLinkWarnSuppressedUntil,
      warnBeforeInternalLinks,
      suppressInternalLinkWarning,
      clearInternalLinkWarningSuppression,

      previewWidth,
      previewQuality,
      previewEncoderType,

      previewFps,
      previewEncoderQuality,
      previewBitrate,

      audioMonitorMix,

      monitorWidth,
      monitorQuality,
      monitorEncoderType,

      monitorFps,
      monitorEncoderQuality,
      monitorBitrate,
    };
  },
  {
    persist: { debug: true },
  },
);

export default useSettingsStore;
